import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { PeriodPresetSchema, type Period, type PeriodPreset } from '@etiquetachef/types';
import { serverDate } from '@etiquetachef/ui-shared/online';

/**
 * Helpers de período do BI super-admin. Portados de `@etiquetachef/ui-shared`
 * (`period/periodPresets.ts` + `period/usePeriod.ts`) para zerar a dependência
 * shadcn no admin (Sprint 39). Tudo em `America/Sao_Paulo` (BRT) sem DST —
 * Brasil aboliu DST em 2019.
 */

const BRT_OFFSET_MS = 3 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const DEFAULT_PRESET: PeriodPreset = 'D30';

/** Meia-noite do dia corrente em BRT, representada como data UTC (só a data importa). */
function nowBrtMidnight(now: Date = serverDate()): Date {
  const brt = new Date(now.getTime() - BRT_OFFSET_MS);
  brt.setUTCHours(0, 0, 0, 0);
  return brt;
}

/** Data (já em BRT, como UTC) → `YYYY-MM-DD`. */
function dateToBrtYyyymmdd(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` → Date UTC à meia-noite; lança se o formato não bate. */
function parseBrtDate(yyyymmdd: string): Date {
  if (!DATE_REGEX.test(yyyymmdd)) {
    throw new Error(`Invalid yyyymmdd: ${yyyymmdd}`);
  }
  const [yyyy, mm, dd] = yyyymmdd.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(yyyy, mm - 1, dd));
}

/** Soma `days` dias inteiros (negativo volta). */
function shiftDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * DAY_MS);
}

/** Dia 1 do mês de `today`. */
function startOfMonthBrt(today: Date): Date {
  return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
}

/** 1º de janeiro do ano de `today`. */
function startOfYearBrt(today: Date): Date {
  return new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
}

/**
 * Converte preset → range BRT (`from`, `to` inclusivos, formato `YYYY-MM-DD`).
 * `today` opcional para testes determinísticos.
 *
 * Origem: sem issue — commit 5ee344f94, 2026-06-09 (porte de
 * `@etiquetachef/ui-shared` para o admin).
 *
 * Contrato: D7/D30/D90/M12 contam o dia de hoje (D7 = hoje e os 6 anteriores).
 * CUSTOM sem datas devolve só hoje — quem tem as datas é `rangeFromQuery`.
 *
 * Usado por: `usePeriod` (via `rangeFromQuery`), `features/shared/PeriodControl.tsx`.
 *
 * Dicas: BRT fixo em −3 h; se o Brasil voltar a ter horário de verão, este
 * offset e os buckets diários das agregações do servidor mudam juntos.
 */
export function presetToRange(
  preset: PeriodPreset,
  today: Date = nowBrtMidnight(),
): { from: string; to: string } {
  const to = dateToBrtYyyymmdd(today);
  switch (preset) {
    case 'TODAY':
      return { from: to, to };
    case 'D7':
      return { from: dateToBrtYyyymmdd(shiftDays(today, -6)), to };
    case 'D30':
      return { from: dateToBrtYyyymmdd(shiftDays(today, -29)), to };
    case 'D90':
      return { from: dateToBrtYyyymmdd(shiftDays(today, -89)), to };
    case 'M12':
      return { from: dateToBrtYyyymmdd(shiftDays(today, -364)), to };
    case 'MTD':
      return { from: dateToBrtYyyymmdd(startOfMonthBrt(today)), to };
    case 'YTD':
      return { from: dateToBrtYyyymmdd(startOfYearBrt(today)), to };
    case 'CUSTOM':
      return { from: to, to };
  }
}

/**
 * Janela imediatamente anterior de mesma duração. Ex.: período D7 → 7 dias
 * anteriores. Usado para deltas comparativos no BI.
 */
export function previousPeriod(period: Period): { from: string; to: string } {
  const from = parseBrtDate(period.from);
  const to = parseBrtDate(period.to);
  const durationDays = Math.round((to.getTime() - from.getTime()) / DAY_MS) + 1;
  const prevTo = shiftDays(from, -1);
  const prevFrom = shiftDays(prevTo, -(durationDays - 1));
  return {
    from: dateToBrtYyyymmdd(prevFrom),
    to: dateToBrtYyyymmdd(prevTo),
  };
}

/**
 * Lê `Period` de uma URLSearchParams com validação. Default = D30 quando
 * `preset` ausente ou inválido. Para CUSTOM, `from`/`to` são obrigatórios; se
 * ausentes, cai para D30.
 */
function rangeFromQuery(query: URLSearchParams): Period {
  const presetRaw = query.get('preset');
  const fromRaw = query.get('from');
  const toRaw = query.get('to');
  const presetParsed = PeriodPresetSchema.safeParse(presetRaw);
  const preset = presetParsed.success ? presetParsed.data : DEFAULT_PRESET;

  if (preset === 'CUSTOM') {
    if (fromRaw && toRaw && DATE_REGEX.test(fromRaw) && DATE_REGEX.test(toRaw)) {
      return { preset, from: fromRaw, to: toRaw };
    }
    const range = presetToRange(DEFAULT_PRESET);
    return { preset: DEFAULT_PRESET, ...range };
  }

  const range = presetToRange(preset);
  return { preset, ...range };
}

/** Serializa `Period` para URLSearchParams com chaves `preset/from/to`. */
function periodToQuery(period: Period): URLSearchParams {
  const params = new URLSearchParams();
  params.set('preset', period.preset);
  params.set('from', period.from);
  params.set('to', period.to);
  return params;
}

/** `from` e `to` em `YYYY-MM-DD` e `from <= to` (comparação de string basta neste formato). */
export function isValidRange(from: string, to: string): boolean {
  if (!DATE_REGEX.test(from) || !DATE_REGEX.test(to)) return false;
  return from <= to;
}

/**
 * Hook que sincroniza `Period` com a query string da URL via `useSearchParams`
 * (react-router 7). O período é fonte da verdade para os dashboards do BI; URL
 * serializa para o link ser compartilhável.
 *
 * Origem: sem issue — commit 5ee344f94, 2026-06-09 (porte de
 * `@etiquetachef/ui-shared` para o admin).
 *
 * Contrato: `period` nunca é inválido (query ruim cai em D30). `setPeriod`
 * mescla `preset/from/to` na query atual preservando os outros parâmetros e
 * cria entrada no histórico (`replace: false` — o "voltar" do navegador volta
 * ao período anterior).
 *
 * Usado por: as Pages de dashboard (`features/dashboard-*`: usage, usage-basic,
 * funnel, sales, finance, churn, product, operation) e
 * `features/shared/PeriodControl.tsx`.
 */
export function usePeriod(): { period: Period; setPeriod: (next: Period) => void } {
  const [searchParams, setSearchParams] = useSearchParams();

  const period = useMemo(() => rangeFromQuery(searchParams), [searchParams]);

  const setPeriod = useCallback(
    (next: Period) => {
      setSearchParams(
        (prev) => {
          const merged = new URLSearchParams(prev);
          const target = periodToQuery(next);
          target.forEach((value, key) => {
            merged.set(key, value);
          });
          return merged;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  return { period, setPeriod };
}
